import React from 'react'
import { Button, Tooltip } from '@material-ui/core'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { useHistory } from 'react-router'
import { useIntl } from 'react-intl'
import PropsTypes from 'prop-types'
import { BUTTON_SIZE_LARGE, BUTTON_SIZE_MEDIUM, BUTTON_SIZE_SMALL } from '../../../utils/constants'

const styles = (theme, size, alternativeDisableColor, bold) => ({
  customButton: {
    padding: theme.spacing(2.5, 0),
    minWidth:
      size === BUTTON_SIZE_LARGE ? 300 : size === BUTTON_SIZE_MEDIUM ? 220 : size === BUTTON_SIZE_SMALL ? 140 : 100,
    borderRadius: 500,
    textTransform: 'none',
    boxShadow: 'none',
    fontSize: '1rem',
    [theme.breakpoints.down('md')]: {
      maxWidth: '260px',
    },
  },
  primaryButton: {
    backgroundColor: theme.palette.button.backgroundColor,
    color: theme.palette.button.color,
    border: `2px solid ${theme.palette.button.backgroundColor}`,
    '&:hover': {
      backgroundColor: theme.palette.button.hoverBackgroundColor,
      color: theme.palette.button.hoverColor,
      border: `2px solid ${theme.palette.button.hoverBorderColor}`,
      boxShadow: 'none',
    },
    '&:disabled': {
      color: alternativeDisableColor ? theme.palette.disabled.alternativeDisableColor : theme.palette.disabled.text,
      backgroundColor: theme.palette.disabled.background,
      border: `2px solid ${theme.palette.disabled.background}`,
    },
  },
  secondaryButton: {
    backgroundColor: theme.palette.button.hoverBackgroundColor,
    color: theme.palette.button.hoverColor,
    border: `2px solid ${theme.palette.button.hoverBorderColor}`,
    '&:hover': {
      backgroundColor: theme.palette.button.backgroundColor,
      color: `${theme.palette.button.color} !important`,
      border: `2px solid ${theme.palette.button.backgroundColor}`,
      boxShadow: 'none',
    },
    '&:disabled': {
      color: theme.palette.disabled.text,
      backgroundColor: '#FFFFFF',
      border: `2px solid ${theme.palette.disabled.background}`,
    },
  },
  linkButton: {
    backgroundColor: 'transparent',
    fontWeight: bold ? 'bold' : 'normal',
    color: theme.palette.primary.main,
    textTransform: 'none',
    boxShadow: 'none',
    padding: 0,
    fontSize: '1rem',
    '&:disabled': {
      color: theme.palette.disabled.text,
      backgroundColor: '#FFFFFF',
    },
    '&:hover': {
      backgroundColor: 'transparent',
      border: 'none',
      boxShadow: 'none',
      textDecoration: 'underline',
    },
  },
  icon: {
    '& .MuiButton-startIcon': {
      animation: '$spin 1500ms infinite linear',
    },
  },
  '@keyframes spin': {
    from: {
      transform: 'rotate(-360deg)',
    },
    to: {
      transform: 'rotate(0deg)',
    },
  },
})

const ButtonComponent = ({
  alternativeDisableColor,
  label,
  icon,
  onClick,
  disabled,
  to,
  hide,
  type,
  size,
  primary,
  linkButton,
  loading,
  loadingText = 'loading',
  translatedText = false,
  bold = true,
  ...props
}) => {
  const useStyles = makeStyles((theme) => styles(theme, size, alternativeDisableColor, bold))

  const classes = useStyles()
  const history = useHistory()
  const intl = useIntl()

  const renderText = () => {
    if (loading) return intl.formatMessage({ id: loadingText })

    if (translatedText) return label

    return intl.formatMessage({ id: label })
  }

  return (
    <Button
      type={type}
      hidden={hide}
      disabled={disabled}
      className={`${linkButton ? classes.linkButton : classes.customButton} ${
        !linkButton && (primary ? classes.primaryButton : classes.secondaryButton)
      } ${loading ? classes.icon : ''}`}
      onClick={type === 'submit' ? null : onClick || (() => history.push(to))}
      variant='contained'
      sx={{ color: 'blue !important' }}
      startIcon={icon}
      {...props}
    >
      {renderText()}
    </Button>
  )
}

ButtonComponent.propTypes = {
  label: PropsTypes.string.isRequired,
  icon: PropsTypes.node,
  onClick: PropsTypes.func,
  disabled: PropsTypes.bool,
  to: PropsTypes.string,
  hide: PropsTypes.bool,
  type: PropsTypes.string,
  size: PropsTypes.string,
  primary: PropsTypes.bool,
  linkButton: PropsTypes.bool,
  loading: PropsTypes.bool,
}

ButtonComponent.defaultProps = {
  icon: null,
  onClick: null,
  disabled: false,
  to: '',
  hide: false,
  type: 'button',
  size: BUTTON_SIZE_SMALL,
  primary: true,
  linkButton: false,
  loading: false,
}

const CustomButtonComponent = ({ tooltip, ...props }) => {
  const intl = useIntl()
  if (!tooltip) {
    return <ButtonComponent {...props} />
  }

  return (
    <Tooltip title={intl.formatMessage({ id: tooltip })}>
      <span>
        <ButtonComponent {...props} />
      </span>
    </Tooltip>
  )
}

CustomButtonComponent.propTypes = {
  tooltip: PropsTypes.string,
}

CustomButtonComponent.defaultProps = {
  tooltip: '',
}

export default CustomButtonComponent
